import { useCallback, useMemo } from 'react';

import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features//logging/logger';
import {
  WRAPPER_TO_PROVIDER_EVENT_TYPES,
  WrapperToProviderEventType,
} from '@/features/event-handlers/wrapper-event-handlers/provider-events/types';
import { Provider } from '@/features/game-context/types';
import { getErrorType } from '@/features/game-window-integration/helpers/error-mapping';
import { handleUnknownEvent } from '@/features/game-window-integration/helpers/generic-helpers';
import { EveriMessages } from '@/features/game-window-integration/types';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';

export const useEveriEventHandlers = () => {
  const handleGameLoaded = useCallback(() => {
    WrapperProxy.setGameLoaded();
  }, []);

  const handleSpinStarted = useCallback(() => {
    WrapperProxy.setSpinInProgress(true);
  }, []);

  const handleSpinEnd = useCallback(() => {
    WrapperProxy.setSpinInProgress(false);
  }, []);

  const handleAudio = useCallback((payload) => {
    WrapperProxy.setAudio(!payload.data.muted);
  }, []);

  const handleError = useCallback((payload) => {
    const errorType = getErrorType(payload.id, Provider.EVERI, false, payload);
    WrapperProxy.setProviderError({
      display: true,
      errorCode: payload.id,
      errorMessage: '',
      errorType,
    });
  }, []);

  const providerEventHandlerMapper = useMemo(
    () => ({
      [EveriMessages.GAME_LOAD_COMPLETED]: handleGameLoaded,
      [EveriMessages.WAGER_REQUESTED]: handleSpinStarted,
      [EveriMessages.RESULT]: handleSpinEnd,
      [EveriMessages.SOUND_CHANGED]: handleAudio,
      [EveriMessages.INSUFFICIENT_FUND]: handleError,
    }),
    [
      handleAudio,
      handleError,
      handleGameLoaded,
      handleSpinEnd,
      handleSpinStarted,
    ],
  );

  const providerEventHandler = useCallback(
    (event: MessageEvent) => {
      if (event.data && typeof event.data === 'string') {
        const parsedEvent = JSON.parse(event.data);
        if (parsedEvent.id && parsedEvent.data) {
          if (providerEventHandlerMapper[parsedEvent.id]) {
            providerEventHandlerMapper[parsedEvent.id]?.(parsedEvent);
          } else {
            handleUnknownEvent(parsedEvent);
          }
        }
      }
    },
    [providerEventHandlerMapper],
  );

  const wrapperEventHandler = useCallback(
    (
      e: MessageEvent<{
        type: WrapperToProviderEventType;
        payload?: string | boolean;
      }>,
    ) => {
      const gameProviderFrame =
        document.querySelector<HTMLIFrameElement>('#game-client')
          ?.contentWindow;
      const executeEvent = (event) => {
        if (gameProviderFrame) {
          gameProviderFrame.postMessage(event, '*');
        } else {
          Logger.rumError({
            name: 'Failed to get handler for provider',
            useCase: DdEventCategory.ProviderIntegration,
            eventType: DdEventType.Error,
            context: {
              provider: Provider.EVERI,
            },
          });
        }
      };

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_PAYTABLE) {
        executeEvent({
          action: 'everi.action.paytable-open',
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.RELOAD_BALANCE) {
        executeEvent({
          action: 'everi.action.balance-update',
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_AUDIO) {
        executeEvent({
          action: e.data.payload
            ? 'everi.action.sound-on'
            : 'everi.action.sound-off',
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_STOP) {
        executeEvent({
          action: 'everi.action.pause',
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_RESUME) {
        executeEvent({
          action: 'everi.action.resume',
        });
      }
    },
    [],
  );

  return { wrapperEventHandler, providerEventHandler };
};
